import React, { Suspense, lazy, useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Sidebar } from './components/Layout/Sidebar'
import { Header } from './components/Layout/Header'
import { NotificationContainer } from './components/Notification'
import { getAdminCsrfToken } from './api/client'
import { authAPI } from './api/endpoints'

const Dashboard = lazy(() => import('./pages/Dashboard'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const MoviesPage = lazy(() => import('./pages/Movies'))
const SeriesPage = lazy(() => import('./pages/Series'))
const EpisodesPage = lazy(() => import('./pages/Episodes'))
const ChannelsPage = lazy(() => import('./pages/Channels'))
const CategoriesPage = lazy(() => import('./pages/Categories'))
const BannersPage = lazy(() => import('./pages/Banners'))
const UsersPage = lazy(() => import('./pages/Users'))
const UserDetailsPage = lazy(() => import('./pages/UserDetails'))
const ProfilesPage = lazy(() => import('./pages/Profiles'))
const SubscriptionsPage = lazy(() => import('./pages/Subscriptions'))
const TrialsPage = lazy(() => import('./pages/Trials'))
const PlansPage = lazy(() => import('./pages/Plans'))
const PaymentsPage = lazy(() => import('./pages/Payments'))
const CouponsPage = lazy(() => import('./pages/Coupons'))
const M3UImporterPage = lazy(() => import('./pages/M3UImporter'))
const TMDbIntegrationPage = lazy(() => import('./pages/TMDbIntegration'))
const NotificationsPage = lazy(() => import('./pages/Notifications'))
const CommentsPage = lazy(() => import('./pages/Comments'))
const LogsPage = lazy(() => import('./pages/Logs'))
const AnalyticsPage = lazy(() => import('./pages/Analytics'))
const SettingsPage = lazy(() => import('./pages/Settings'))
const AdminProfilePage = lazy(() => import('./pages/AdminProfile'))
const ParentalControlPage = lazy(() => import('./pages/ParentalControl'))

const PageLoader = () => (
  <div className="flex min-h-[40vh] items-center justify-center text-nexus-text-secondary">
    Carregando...
  </div>
)

function App() {
  const [checkingSession, setCheckingSession] = useState(!getAdminCsrfToken())

  useEffect(() => {
    if (!checkingSession) return
    authAPI.restoreSession()
      .then((response) => {
        sessionStorage.setItem('admin-csrf', response.data.csrf_token)
      })
      .catch(() => sessionStorage.removeItem('admin-csrf'))
      .finally(() => setCheckingSession(false))
  }, [checkingSession])

  const isAuthenticated = !!getAdminCsrfToken()

  return (
    <Router>
      <NotificationContainer />

      <Routes>
        <Route
          path="/login"
          element={
            <Suspense fallback={<PageLoader />}>
              <LoginPage />
            </Suspense>
          }
        />
        <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPasswordPage /></Suspense>} />
        <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPasswordPage /></Suspense>} />

        <Route
          path="/*"
          element={
            checkingSession ? (
              <PageLoader />
            ) : isAuthenticated ? (
              <div className="flex min-h-screen bg-nexus-bg text-nexus-text">
                <Sidebar />
                <div className="flex min-w-0 flex-1 flex-col">
                  <Header title="NEXUS Streaming" subtitle="Centro de operação" />
                  <main className="flex-1 overflow-y-auto bg-nexus-bg">
                    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/movies" element={<MoviesPage />} />
                          <Route path="/movies/new" element={<MoviesPage />} />
                          <Route path="/movies/:id" element={<MoviesPage />} />
                          <Route path="/series" element={<SeriesPage />} />
                          <Route path="/series/new" element={<SeriesPage />} />
                          <Route path="/series/:id" element={<SeriesPage />} />
                          <Route path="/episodes" element={<EpisodesPage />} />
                          <Route path="/channels" element={<ChannelsPage />} />
                          <Route path="/categories" element={<CategoriesPage />} />
                          <Route path="/banners" element={<BannersPage />} />
                          <Route path="/users" element={<UsersPage />} />
                          <Route path="/users/:id" element={<UserDetailsPage />} />
                          <Route path="/profiles" element={<ProfilesPage />} />
                          <Route path="/trials" element={<TrialsPage />} />
                          <Route path="/plans" element={<PlansPage />} />
                          <Route path="/subscriptions" element={<SubscriptionsPage />} />
                          <Route path="/payments" element={<PaymentsPage />} />
                          <Route path="/coupons" element={<CouponsPage />} />
                          <Route path="/importer" element={<M3UImporterPage />} />
                          <Route path="/tmdb" element={<TMDbIntegrationPage />} />
                          <Route path="/notifications" element={<NotificationsPage />} />
                          <Route path="/comments" element={<CommentsPage />} />
                          <Route path="/analytics" element={<AnalyticsPage />} />
                          <Route path="/logs" element={<LogsPage />} />
                          <Route path="/settings" element={<SettingsPage />} />
                          <Route path="/parental" element={<ParentalControlPage />} />
                          <Route path="/profile" element={<AdminProfilePage />} />
                          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </Suspense>
                    </div>
                  </main>
                </div>
              </div>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </Router>
  )
}

export default App
